import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { useCurrentUserOrNull } from '@/shared/auth/hooks';
import { parsePanelStack, serializePanelStack } from '@/shared/components/side-panel';
import { useFormatters } from '@/shared/i18n/format';

import {
  useCrmMe,
  type CrmMe,
  type CustomField,
  type DynamicType,
  type DynamicTypeAccess,
  type EntityType,
  type LeadStatus,
  type ListQuery,
  type Pipeline,
  type Stage,
} from './api';
import { panelTypeOf } from './model/entities';
import { customFilterKeys, FILTER_KEYS, presetQuery, PRESETS, type PresetId } from './model/filters';
import { formatCompactMoney, formatMoney } from './model/money';
import { HRYVNIA } from './model/products';

/** The CRM configuration of the caller with lookups by id (stages, statuses, lists). */
export interface CrmConfig {
  me: CrmMe | undefined;
  isLoading: boolean;
  isError: boolean;
  hasAccess: boolean;
  pipeline: (id: string | null | undefined) => Pipeline | undefined;
  defaultPipeline: Pipeline | undefined;
  /** The pipelines the caller may create and move deals in (the pipeline matrix; all of them without restrictions). */
  creatablePipelines: Pipeline[];
  /** The default pipeline among `creatablePipelines` (new deals and conversions go there). */
  defaultCreatePipeline: Pipeline | undefined;
  stage: (id: string | null | undefined) => Stage | undefined;
  status: (id: string | null | undefined) => LeadStatus | undefined;
  sourceName: (id: string | null | undefined) => string | undefined;
  lossReasonName: (id: string | null | undefined) => string | undefined;
  canCreate: (type: EntityType) => boolean;
  /** «Use leads»: false while the lead functionality is switched off (no Leads in the menu and «create» buttons; lead routes go to the CRM start). */
  leadsEnabled: boolean;
  baseCurrency: string;
  /** Custom fields of a type in order (items: those of the user-defined type `typeId`). */
  fields: (type: EntityType, typeId?: string | null) => CustomField[];
  /** The user-defined entity types the caller may use («Свои сущности»), in order. */
  entityTypes: DynamicTypeAccess[];
  /** A user-defined type by id or code. */
  entityType: (idOrCode: string | null | undefined) => DynamicType | undefined;
  /** A stage of a user-defined type's pipelines. */
  itemStage: (id: string | null | undefined) => Stage | undefined;
  /** Whether the caller may create items of a user-defined type. */
  canCreateItem: (typeId: string | null | undefined) => boolean;
}

export function useCrmConfig(): CrmConfig {
  const query = useCrmMe();
  const me = query.data;
  return useMemo<CrmConfig>(() => {
    const stages = new Map((me?.pipelines ?? []).flatMap((p) => p.stages).map((s) => [s.id, s]));
    const statuses = new Map((me?.leadStatuses ?? []).map((s) => [s.id, s]));
    const entityTypes = [...(me?.dynamicTypes ?? [])].sort((a, b) => a.type.sortOrder - b.type.sortOrder);
    const itemStages = new Map(entityTypes.flatMap((t) => t.type.pipelines).flatMap((p) => p.stages).map((s) => [s.id, s]));
    return {
      me,
      isLoading: query.isLoading,
      isError: query.isError,
      hasAccess: !!me && me.role !== 'none',
      pipeline: (id) => me?.pipelines.find((p) => p.id === id),
      defaultPipeline: me?.pipelines.find((p) => p.isDefault) ?? me?.pipelines[0],
      creatablePipelines: (me?.pipelines ?? []).filter((p) => p.canEdit !== false),
      defaultCreatePipeline:
        (me?.pipelines ?? []).find((p) => p.isDefault && p.canEdit !== false) ?? (me?.pipelines ?? []).find((p) => p.canEdit !== false),
      stage: (id) => (id ? stages.get(id) : undefined),
      status: (id) => (id ? statuses.get(id) : undefined),
      sourceName: (id) => me?.sources.find((s) => s.id === id)?.name,
      lossReasonName: (id) => me?.lossReasons.find((s) => s.id === id)?.name,
      canCreate: (type) =>
        type === 'item'
          ? entityTypes.some((t) => t.access.canCreate)
          : !!me?.access[type].canCreate && (type !== 'lead' || me.useLeads !== false),
      leadsEnabled: me?.useLeads !== false,
      baseCurrency: me?.baseCurrency ?? HRYVNIA,
      fields: (type, typeId) =>
        (me?.customFields ?? [])
          .filter((f) => f.entityType === type && (type !== 'item' || f.dynamicTypeId === typeId))
          .sort((a, b) => a.sortOrder - b.sortOrder),
      entityTypes,
      entityType: (idOrCode) => entityTypes.find((t) => t.type.id === idOrCode || t.type.code === idOrCode)?.type,
      itemStage: (id) => (id ? itemStages.get(id) : undefined),
      canCreateItem: (typeId) => entityTypes.some((t) => t.type.id === typeId && t.access.canCreate),
    };
  }, [me, query.isLoading, query.isError]);
}

/** Money formatting bound to the UI language. */
export function useMoney() {
  const { intlLocale } = useFormatters();
  return useMemo(
    () => ({
      format: (amount: number | null | undefined, currency: string) => formatMoney(amount, currency, intlLocale),
      compact: (amount: number, currency: string) => formatCompactMoney(amount, currency, intlLocale),
    }),
    [intlLocale],
  );
}

/** The columns a list's table shows: the table publishes them, the toolbar saves them with a view. */
export interface ListColumns {
  /** The ids of the visible columns in the table's order (null until the table has published them). */
  visible: string[] | null;
  publish: (ids: string[]) => void;
}

export interface ListStateApi {
  type: EntityType;
  preset: string;
  /** Raw filter values from the URL (strings). */
  values: Record<string, string | undefined>;
  page: number;
  /** What the API gets: the preset's filters, the explicit ones, sort and page. */
  query: ListQuery;
  update: (patch: Record<string, string | null | undefined>) => void;
  setPreset: (preset: string, query?: Record<string, string>) => void;
  reset: () => void;
  columns: ListColumns;
}

/**
 * The toolbar state of a CRM list or board in the page URL (`?preset=my&semantic=open&q=…&page=2`): shareable, kept on
 * reload, the open panels (`panel=`) stay untouched. A saved preset is applied by copying its query into the URL.
 */
export function useListState(type: EntityType, fixed: ListQuery = {}): ListStateApi {
  const [params, setParams] = useSearchParams();
  const me = useCurrentUserOrNull();
  const config = useCrmConfig();
  const fields = config.fields(type, typeof fixed.typeId === 'string' ? fixed.typeId : null);
  const fieldKeys = fields.filter((f) => f.showInFilter).flatMap(customFilterKeys).join('|');
  const keys = useMemo(() => [...FILTER_KEYS[type], ...(fieldKeys ? fieldKeys.split('|') : [])], [type, fieldKeys]);
  const preset = params.get('preset') ?? 'all';
  const values = useMemo(() => {
    const result: Record<string, string | undefined> = {};
    for (const key of keys) result[key] = params.get(key === 'search' ? 'q' : key) ?? undefined;
    return result;
  }, [params, keys]);
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);
  const query = useMemo<ListQuery>(() => {
    const builtIn = (PRESETS[type] as string[]).includes(preset) ? presetQuery(type, preset as PresetId, me?.id) : {};
    const explicit = Object.fromEntries(Object.entries(values).filter(([, v]) => v !== undefined && v !== ''));
    return { ...builtIn, ...explicit, ...fixed, page };
  }, [type, preset, me?.id, values, fixed, page]);

  const write = useCallback(
    (mutate: (next: URLSearchParams) => void) =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          mutate(next);
          return next;
        },
        { replace: true, preventScrollReset: true },
      ),
    [setParams],
  );

  const update = useCallback(
    (patch: Record<string, string | null | undefined>) =>
      write((next) => {
        for (const [key, value] of Object.entries(patch)) {
          const name = key === 'search' ? 'q' : key;
          if (value === null || value === undefined || value === '') next.delete(name);
          else next.set(name, value);
        }
        if (!('page' in patch)) next.delete('page');
      }),
    [write],
  );

  const setPreset = useCallback(
    (id: string, saved?: Record<string, string>) =>
      write((next) => {
        for (const key of [...keys, 'page']) next.delete(key === 'search' ? 'q' : key);
        if (id === 'all') next.delete('preset');
        else next.set('preset', id);
        for (const [key, value] of Object.entries(saved ?? {})) {
          if (keys.includes(key)) next.set(key === 'search' ? 'q' : key, value);
        }
      }),
    [write, keys],
  );

  const reset = useCallback(() => setPreset('all'), [setPreset]);
  const [visibleColumns, publishColumns] = useState<string[] | null>(null);
  const columns = useMemo<ListColumns>(() => ({ visible: visibleColumns, publish: publishColumns }), [visibleColumns]);
  return { type, preset, values, page, query, update, setPreset, reset, columns };
}

/** Prefill parameters of the `*:new` panels. */
export const NEW_RECORD_PARAMS = [
  'crmType',
  'crmPipeline',
  'crmStage',
  'crmStatus',
  'crmCompany',
  'crmContact',
  'crmTitle',
  'crmDeal',
  'crmLead',
  'crmQuote',
  'crmFirstName',
  'crmLastName',
  'crmPhone',
  'crmEmail',
] as const;

/** Opens the new-record panel of a type on top of the current panels, optionally prefilled. */
export function useOpenNewRecord() {
  const location = useLocation();
  const navigate = useNavigate();
  return useCallback(
    (type: EntityType, prefill: Partial<Record<(typeof NEW_RECORD_PARAMS)[number], string>> = {}) => {
      const params = new URLSearchParams(location.search);
      for (const key of NEW_RECORD_PARAMS) params.delete(key);
      for (const [key, value] of Object.entries(prefill)) if (value) params.set(key, value);
      const stack = parsePanelStack(location.search).filter((entry) => entry.id !== 'new');
      const search = serializePanelStack(params.toString(), [...stack, { type: panelTypeOf(type), id: 'new' }]);
      void navigate(
        { pathname: location.pathname, search },
        { state: { sidePanelPushed: stack.length + 1 }, preventScrollReset: true },
      );
    },
    [location.pathname, location.search, navigate],
  );
}

/** Replaces the `type:new` panel with the created record's card and drops the prefill parameters. */
export function useShowCreated() {
  const location = useLocation();
  const navigate = useNavigate();
  return useCallback(
    (type: EntityType, id: string) => {
      const search = new URLSearchParams(location.search);
      for (const key of NEW_RECORD_PARAMS) search.delete(key);
      const stack = parsePanelStack(location.search).filter((entry) => entry.key !== `${panelTypeOf(type)}:new`);
      void navigate(
        { pathname: location.pathname, search: serializePanelStack(search.toString(), [...stack, { type: panelTypeOf(type), id }]) },
        { replace: true, state: location.state as unknown, preventScrollReset: true },
      );
    },
    [location.pathname, location.search, location.state, navigate],
  );
}
